import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Lock, Eye, EyeOff, ShieldCheck, CheckCircle2 } from "lucide-react";
import { Logo, LogoFull } from "@/components/Logo";
import BRAND from "@/lib/brand";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/context/i18n";

function Shell({ title, subtitle, children, footer }) {
  const t = useT();
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden navy-grain p-12 text-white lg:flex lg:flex-col lg:justify-between">
        <Logo light showTagline size={44} />
        <div className="my-8">
          <div className="overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
            <video data-testid="auth-video" src={BRAND.assets.video} autoPlay muted loop playsInline preload="auto" className="h-full w-full object-cover" />
          </div>
          <h2 className="font-display mt-6 text-3xl font-extrabold leading-tight tracking-tight">{t("Manage all your loans, EMIs & payments in one smart, secure place.")}</h2>
          <p className="mt-3 max-w-md text-slate-300">{t("Track loans, never miss an EMI, pay securely and earn rewards.")}</p>
        </div>
        <p className="text-xs text-slate-500">{t("Manage your loans, EMIs and rewards in one place.")}</p>
      </div>
      <div className="relative flex flex-col justify-center bg-[#F7F5EF] px-6 py-10 sm:px-12">
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-6 flex justify-center lg:justify-start"><LogoFull className="h-20 w-auto" /></div>
          <div className="mb-6 overflow-hidden rounded-xl border border-slate-200 shadow-sm lg:hidden">
            <video data-testid="auth-video-mobile" src={BRAND.assets.video} autoPlay muted loop playsInline preload="auto" className="h-40 w-full object-cover" />
          </div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-[#0B132B]">{title}</h1>
          <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
          <div className="mt-7">{children}</div>
          {footer && <div className="mt-6 text-center text-sm text-slate-500">{footer}</div>}
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-md border border-slate-200 px-3.5 py-2.5 text-sm text-[#0B132B] outline-none transition focus:border-[#14B8A6] focus:ring-1 focus:ring-[#14B8A6]";
const btnCls = "flex w-full items-center justify-center gap-2 rounded-full bg-[#1F7A54] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#186445] disabled:opacity-60";

function PasswordInput({ value, onChange, testid, placeholder, minLength, light = false }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input data-testid={testid} type={show ? "text" : "password"} required minLength={minLength}
        className={inputCls + (light ? " bg-white" : "") + " pr-10"} value={value} onChange={onChange} placeholder={placeholder} />
      <button type="button" tabIndex={-1} aria-label={show ? "Hide password" : "Show password"} data-testid={`${testid}-toggle`}
        onClick={() => setShow((s) => !s)} className="absolute inset-y-0 right-0 grid w-10 place-items-center text-slate-400 hover:text-slate-600">
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

function GoogleButton({ label = "Continue with Google" }) {
  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div>
      <div className="my-4 flex items-center gap-3 text-xs font-medium text-slate-400">
        <span className="h-px flex-1 bg-slate-200" /> or <span className="h-px flex-1 bg-slate-200" />
      </div>
      <button type="button" onClick={google} data-testid="google-signin"
        className="flex w-full items-center justify-center gap-2.5 rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-[#0B132B] transition-colors hover:bg-slate-50">
        <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
          <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.4 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
          <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.4 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
          <path fill="#4CAF50" d="M24 44c5.4 0 10.3-2.1 14-5.4l-6.5-5.5C29.6 34.6 26.9 36 24 36c-5.2 0-9.6-3.3-11.2-7.9l-6.6 5.1C9.6 39.6 16.2 44 24 44z"/>
          <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.5 5.5c-.5.4 6.3-4.6 6.3-15 0-1.2-.1-2.3-.4-3.5z"/>
        </svg>
        {label}
      </button>
    </div>
  );
}

function LoanLookupButton() {
  const nvg = useNavigate();
  return (
    <button type="button" onClick={() => nvg("/loan-lookup")} data-testid="loan-lookup-btn"
      className="mt-3 flex w-full items-center justify-center gap-2.5 rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-[#0B132B] transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#14B8A6]">
      <ShieldCheck size={18} className="text-[#14B8A6]" />
      Login with OTP
    </button>
  );
}

export function Login() {
  const { loginWithToken } = useAuth();
  const t = useT();
  const nvg = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", f);
      loginWithToken(data.token, data.user);
      toast.success("Welcome back!");
      nvg("/dashboard");
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };
  return (
    <Shell title={t("Welcome back")} subtitle={t("Log in to your ZRo Pe account")}
      footer={<>{t("New to ZRo Pe?")} <Link to="/register" className="font-semibold text-[#14B8A6]">{t("Create account")}</Link></>}>
      <form onSubmit={submit} className="space-y-4">
        <div><label className="text-sm font-medium text-slate-700">{t("Email")}</label><input data-testid="login-email" type="email" required autoComplete="email" className={inputCls} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="you@example.com" /></div>
        <div><label className="text-sm font-medium text-slate-700">{t("Password")}</label><PasswordInput testid="login-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder={t("Enter your password")} /></div>
        <div className="text-right -mt-1"><Link to="/forgot-password" data-testid="forgot-link" className="text-xs font-semibold text-[#14B8A6] hover:underline">{t("Forgot password?")}</Link></div>
        <button data-testid="login-submit" className={btnCls} disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{t("Log in")} <ArrowRight size={16} /></>}</button>
      </form>
      <GoogleButton />
      <LoanLookupButton />
    </Shell>
  );
}

export function Register() {
  const { loginWithToken } = useAuth();
  const t = useT();
  const nvg = useNavigate();
  const [step, setStep] = useState("form");
  const [f, setF] = useState({ name: "", email: "", mobile: "", password: "", referral_code: "" });
  const [loading, setLoading] = useState(false);
  const [reg, setReg] = useState(null);
  const [ecode, setEcode] = useState("");
  const [motp, setMotp] = useState("");
  const [ev, setEv] = useState(false);
  const [mv, setMv] = useState(false);
  const [busy, setBusy] = useState("");

  const start = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/auth/register/start", f);
      setReg(data); setEcode(""); setMotp(""); setEv(false); setMv(false); setStep("verify");
      toast.success("Verification codes sent to your email & mobile.");
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };
  const verifyEmail = async () => {
    setBusy("email");
    try { await api.post("/auth/register/verify-email", { reg_id: reg.reg_id, code: ecode }); setEv(true); toast.success("Email verified"); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };
  const verifyMobile = async () => {
    setBusy("mobile");
    try { await api.post("/auth/register/verify-mobile", { reg_id: reg.reg_id, code: motp }); setMv(true); toast.success("Mobile verified"); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };
  const resendEmail = async () => {
    try { const { data } = await api.post("/auth/register/resend-email", { reg_id: reg.reg_id }); setReg({ ...reg, email_demo_code: data.email_demo_code }); toast.success("Email code resent"); }
    catch (err) { toast.error(apiError(err)); }
  };
  const resendMobile = async () => {
    try { const { data } = await api.post("/auth/register/resend-mobile", { reg_id: reg.reg_id }); setReg({ ...reg, mobile_demo_otp: data.mobile_demo_otp }); toast.success("OTP resent"); }
    catch (err) { toast.error(apiError(err)); }
  };
  useEffect(() => {
    if (ev && mv && reg) {
      (async () => {
        try { const { data } = await api.post("/auth/register/complete", { reg_id: reg.reg_id }); loginWithToken(data.token, data.user); toast.success("Account created!"); nvg("/dashboard"); }
        catch (err) { toast.error(apiError(err)); }
      })();
    }
  }, [ev, mv]); // eslint-disable-line react-hooks/exhaustive-deps

  if (step === "verify") return (
    <Shell title="Verify your account" subtitle="Confirm your email and mobile to activate your account"
      footer={<button onClick={() => setStep("form")} data-testid="reg-edit-details" className="font-semibold text-[#14B8A6]">Edit details</button>}>
      <div className="space-y-5">
        <div className="rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-[#0B132B]">Email — {f.email}</span>
            {ev && <span data-testid="reg-email-verified" className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><CheckCircle2 size={14} /> Verified</span>}
          </div>
          {reg?.email_demo_code && !ev && <div data-testid="reg-demo-email" className="mt-2 rounded bg-amber-50 p-2 text-xs text-amber-800">Email code: <b className="tracking-widest">{reg.email_demo_code}</b></div>}
          {!ev && <>
            <div className="mt-2 flex gap-2">
              <input data-testid="reg-email-code" inputMode="numeric" maxLength={6} value={ecode} onChange={(e) => setEcode(e.target.value.replace(/\D/g, ""))} className={inputCls} placeholder="6-digit email code" />
              <button data-testid="reg-verify-email" onClick={verifyEmail} disabled={busy === "email" || ecode.length !== 6} className="shrink-0 rounded-md bg-[#1F7A54] px-4 text-sm font-semibold text-white hover:bg-[#186445] disabled:opacity-60">Verify</button>
            </div>
            <button data-testid="reg-resend-email" onClick={resendEmail} className="mt-2 text-xs font-semibold text-[#14B8A6]">Resend code</button>
          </>}
        </div>
        <div className="rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-[#0B132B]">Mobile — {f.mobile}</span>
            {mv && <span data-testid="reg-mobile-verified" className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><CheckCircle2 size={14} /> Verified</span>}
          </div>
          {reg?.mobile_demo_otp && !mv && <div data-testid="reg-demo-mobile" className="mt-2 rounded bg-amber-50 p-2 text-xs text-amber-800">Mobile OTP: <b className="tracking-widest">{reg.mobile_demo_otp}</b></div>}
          {!mv && <>
            <div className="mt-2 flex gap-2">
              <input data-testid="reg-mobile-otp" inputMode="numeric" maxLength={6} value={motp} onChange={(e) => setMotp(e.target.value.replace(/\D/g, ""))} className={inputCls} placeholder="6-digit mobile OTP" />
              <button data-testid="reg-verify-mobile" onClick={verifyMobile} disabled={busy === "mobile" || motp.length !== 6} className="shrink-0 rounded-md bg-[#1F7A54] px-4 text-sm font-semibold text-white hover:bg-[#186445] disabled:opacity-60">Verify</button>
            </div>
            <button data-testid="reg-resend-mobile" onClick={resendMobile} className="mt-2 text-xs font-semibold text-[#14B8A6]">Resend OTP</button>
          </>}
        </div>
        {ev && mv && <div className="flex items-center justify-center gap-2 text-sm text-slate-500" data-testid="reg-activating"><Loader2 className="h-4 w-4 animate-spin" /> Activating your account…</div>}
      </div>
    </Shell>
  );

  return (
    <Shell title={t("Create your account")} subtitle={t("Start managing your loans in minutes")}
      footer={<>{t("Already have an account?")} <Link to="/login" className="font-semibold text-[#14B8A6]">{t("Log in")}</Link></>}>
      <form onSubmit={start} className="space-y-4">
        <div><label className="text-sm font-medium text-slate-700">{t("Full name")}</label><input data-testid="reg-name" required className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Virat Sharma" /></div>
        <div><label className="text-sm font-medium text-slate-700">{t("Email")}</label><input data-testid="reg-email" type="email" required className={inputCls} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="you@example.com" /></div>
        <div><label className="text-sm font-medium text-slate-700">{t("Mobile")}</label><input data-testid="reg-mobile" required inputMode="numeric" maxLength={10} className={inputCls} value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })} placeholder="10-digit mobile (starts 6-9)" /></div>
        <div><label className="text-sm font-medium text-slate-700">{t("Password")}</label><PasswordInput testid="reg-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="Min 8 characters" minLength={8} /></div>
        <div><label className="text-sm font-medium text-slate-700">Referral code <span className="text-slate-400">(optional)</span></label><input data-testid="reg-referral" className={inputCls} value={f.referral_code} onChange={(e) => setF({ ...f, referral_code: e.target.value.toUpperCase() })} placeholder="e.g. ROKO007" /></div>
        <button data-testid="reg-submit" className={btnCls} disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Create account")}</button>
      </form>
      <GoogleButton />
      <LoanLookupButton />
    </Shell>
  );
}

export function LoanLookup({ authed }) {
  const nvg = useNavigate();
  const [f, setF] = useState({ mobile: "", loan_number: "", lender: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/auth/lookup", f);
      nvg(authed ? "/link-loan/verify" : "/otp-verification", { state: { ...data, mobile: f.mobile } });
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };
  return (
    <Shell title="Check my loan" subtitle="Securely locate your loans to continue"
      footer={authed
        ? <Link to="/loans" className="inline-flex items-center gap-1 font-semibold text-[#14B8A6]"><ArrowLeft size={14} /> Back to my loans</Link>
        : <Link to="/" className="inline-flex items-center gap-1 font-semibold text-[#14B8A6]"><ArrowLeft size={14} /> Back home</Link>}>
      <form onSubmit={submit} className="space-y-4">
        <div><label className="text-sm font-medium text-slate-700">Mobile Number *</label><input data-testid="lookup-mobile" required className={inputCls} value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} placeholder="10-digit mobile" /></div>
        <div><label className="text-sm font-medium text-slate-700">Loan Number <span className="text-slate-400">(optional)</span></label><input data-testid="lookup-loan" className={inputCls} value={f.loan_number} onChange={(e) => setF({ ...f, loan_number: e.target.value })} placeholder="e.g. HDFC-00123" /></div>
        <div><label className="text-sm font-medium text-slate-700">Lender / NBFC <span className="text-slate-400">(optional)</span></label><input data-testid="lookup-lender" className={inputCls} value={f.lender} onChange={(e) => setF({ ...f, lender: e.target.value })} placeholder="e.g. HDFC Bank" /></div>
        <button data-testid="lookup-submit" className={btnCls} disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue <ArrowRight size={16} /></>}</button>
      </form>
      <p className="mt-4 text-xs text-slate-400">Aadhaar / PAN verification will be added only with a compliant provider. We never collect them here.</p>
    </Shell>
  );
}

export function OtpVerification({ authed }) {
  const nvg = useNavigate();
  const { state } = useLocation();
  const { loginWithToken, user } = useAuth();
  const alreadyAuthed = authed || !!user;
  const [session, setSession] = useState(state || {});
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(30);
  useState(() => { const id = setInterval(() => setTimer((t) => (t > 0 ? t - 1 : 0)), 1000); return () => clearInterval(id); });

  if (!session?.session_id) return <Shell title="Session expired" subtitle="Please start the lookup again" footer={<Link to={alreadyAuthed ? "/link-loan" : "/loan-lookup"} className="font-semibold text-[#14B8A6]">Go to Loan Lookup</Link>}><div /></Shell>;

  const verify = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { session_id: session.session_id, otp });
      if (alreadyAuthed) {
        // Already signed in — never replace the current session; just return to their loans.
        toast.success("Loans linked to your account.");
        nvg("/loans");
        return;
      }
      if (data.new_user) { toast.info("No account found for this number. Please register."); nvg("/register"); return; }
      loginWithToken(data.token, data.user);
      toast.success("Verified!");
      nvg("/dashboard");
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };
  const resend = async () => {
    try { const { data } = await api.post("/auth/resend-otp", { session_id: session.session_id }); setSession({ ...session, ...data }); setTimer(30); toast.success("OTP resent"); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Shell title="Verify OTP" subtitle={`Enter the 6-digit code sent to ${session.mobile}`}
      footer={<Link to={alreadyAuthed ? "/link-loan" : "/loan-lookup"} className="font-semibold text-[#14B8A6]">Change mobile number</Link>}>
      {session.demo_otp && <div className="mb-4 rounded-md bg-amber-50 p-3 text-sm text-amber-800" data-testid="demo-otp">Your OTP: <b className="tracking-widest">{session.demo_otp}</b></div>}
      <form onSubmit={verify} className="space-y-4">
        <input data-testid="otp-input" inputMode="numeric" maxLength={6} required autoFocus
          className="w-full rounded-md border border-slate-200 px-3.5 py-3 text-center text-2xl font-bold tracking-[0.5em] text-[#0B132B] outline-none focus:border-[#14B8A6] focus:ring-1 focus:ring-[#14B8A6]"
          value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} placeholder="••••••" />
        <button data-testid="otp-verify" className={btnCls} disabled={loading || otp.length !== 6}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & continue"}</button>
      </form>
      <div className="mt-4 text-center text-sm text-slate-500">
        {timer > 0 ? <>Resend OTP in {timer}s</> : <button onClick={resend} data-testid="otp-resend" className="font-semibold text-[#14B8A6]">Resend OTP</button>}
      </div>
    </Shell>
  );
}

function ForgotPasswordForm({ scope, loginPath }) {
  const t = useT();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email, scope });
      toast.success(data.message || "If an account exists, we've sent reset instructions.");
      setSent(true);
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };
  return (
    <Shell title={t("Forgot password?")} subtitle={scope === "admin" ? "Reset your admin account password" : t("We'll email you a secure reset link")}
      footer={<Link to={loginPath} className="inline-flex items-center gap-1 font-semibold text-[#14B8A6]"><ArrowLeft size={14} /> {t("Back to login")}</Link>}>
      {sent ? (
        <div className="rounded-md bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="forgot-sent">
          {t("If an account exists for this email, we have sent password reset instructions. Please check your inbox.")}
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div><label className="text-sm font-medium text-slate-700">{t("Email")}</label>
            <input data-testid="forgot-email" type="email" required autoComplete="email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" /></div>
          <button data-testid="forgot-submit" className={btnCls} disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{t("Send Reset Link")} <ArrowRight size={16} /></>}</button>
        </form>
      )}
    </Shell>
  );
}

export function ForgotPassword() { return <ForgotPasswordForm scope="customer" loginPath="/login" />; }
export function AdminForgotPassword() { return <ForgotPasswordForm scope="admin" loginPath="/admin/login" />; }

export function ResetPassword() {
  const t = useT();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("checking"); // checking | valid | invalid | done
  const [scope, setScope] = useState("customer");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    api.get(`/auth/reset-password/validate?token=${encodeURIComponent(token)}`)
      .then((r) => { if (r.data.valid) { setState("valid"); setScope(r.data.scope || "customer"); } else setState("invalid"); })
      .catch(() => setState("invalid"));
  }, [token]);
  const loginPath = scope === "admin" ? "/admin/login" : "/login";
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error(t("Password must be at least 8 characters"));
    if (pw !== confirm) return toast.error(t("Passwords do not match"));
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setState("done");
    } catch (err) { toast.error(apiError(err)); if (String(err?.response?.data?.detail || "").includes("expired")) setState("invalid"); }
    finally { setLoading(false); }
  };
  if (state === "checking") return <Shell title={t("Reset password")} subtitle={t("Verifying your reset link…")}><div /></Shell>;
  if (state === "invalid") return (
    <Shell title={t("Link invalid or expired")} subtitle={t("Password reset link is invalid or has expired.")}>
      <div data-testid="reset-invalid" className="rounded-md bg-red-50 p-4 text-sm text-red-700">{t("This password reset link is invalid or has expired.")}</div>
      <Link to="/forgot-password" data-testid="request-new-link" className={btnCls + " mt-4 no-underline"}>{t("Request New Reset Link")}</Link>
    </Shell>
  );
  if (state === "done") return (
    <Shell title={t("Password reset")} subtitle={t("Your password has been reset successfully.")}>
      <div data-testid="reset-done" className="rounded-md bg-emerald-50 p-4 text-sm text-emerald-800">{t("Your password has been reset successfully.")}</div>
      <Link to={loginPath} data-testid="reset-login" className={btnCls + " mt-4 no-underline"}>{t("Log In")}</Link>
    </Shell>
  );
  return (
    <Shell title={t("Reset password")} subtitle={t("Choose a new password for your account")}>
      <form onSubmit={submit} className="space-y-4">
        <div><label className="text-sm font-medium text-slate-700">{t("New Password")}</label><PasswordInput testid="reset-password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Min 8 characters" minLength={8} /></div>
        <div><label className="text-sm font-medium text-slate-700">{t("Confirm New Password")}</label><PasswordInput testid="reset-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={t("Re-enter new password")} minLength={8} /></div>
        <button data-testid="reset-submit" className={btnCls} disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Reset Password")}</button>
      </form>
    </Shell>
  );
}

export function AdminLogin() {
  const { loginWithToken } = useAuth();
  const nvg = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState("creds"); // creds | setup | otp
  const [pending, setPending] = useState("");   // pending_2fa token
  const [maskEmail, setMaskEmail] = useState("");
  const [setupEmail, setSetupEmail] = useState("");
  const [emailLocked, setEmailLocked] = useState(false);
  const [otp, setOtp] = useState("");
  const [cooldown, setCooldown] = useState(0);

  const auth = () => ({ headers: { Authorization: `Bearer ${pending}` } });

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => (c <= 1 ? 0 : c - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const showDemo = (d) => { if (d?.demo_otp) toast.message(`Preview code: ${d.demo_otp}`, { duration: 8000 }); };

  const submitCreds = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/auth/admin-login", f);
      if (data.token) { loginWithToken(data.token, data.user); toast.success("Admin access granted"); nvg("/admin/dashboard"); return; }
      setPending(data.pending_token);
      setCooldown(data.resend_cooldown || 30);
      if (data.status === "PENDING_2FA") {
        setMaskEmail(data.email_masked || ""); setPhase("otp"); showDemo(data);
        toast.success("We sent a verification code to your 2FA email");
      } else {
        setSetupEmail(data.suggested_email || ""); setEmailLocked(!!data.email_locked); setPhase("setup");
      }
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };

  const sendSetup = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/auth/admin/2fa/setup-send", { email: setupEmail }, auth());
      setMaskEmail(data.email_masked || ""); setOtp(""); setCooldown(30); setPhase("otp"); showDemo(data);
      toast.success("Verification code sent");
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };

  const verify = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/auth/admin/2fa/verify", { code: otp }, auth());
      loginWithToken(data.token, data.user);
      toast.success("Admin access granted");
      nvg("/admin/dashboard");
    } catch (err) { toast.error(apiError(err)); setOtp(""); } finally { setLoading(false); }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    try {
      const { data } = await api.post("/auth/admin/2fa/resend", {}, auth());
      setMaskEmail(data.email_masked || maskEmail); setCooldown(30); showDemo(data);
      toast.success("A new code is on its way");
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="grid min-h-screen place-items-center navy-grain px-4">
      <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#1C2541] p-8">
        <Logo light showTagline size={44} />
        <div className="mt-6 inline-flex items-center gap-2 rounded-md bg-white/5 px-3 py-1 text-xs font-semibold text-[#14B8A6]"><Lock size={13} /> Admin Console</div>

        {phase === "creds" && (
          <>
            <h1 className="font-display mt-3 text-2xl font-bold text-white">Staff sign in</h1>
            <form onSubmit={submitCreds} className="mt-6 space-y-4">
              <input data-testid="admin-email" type="email" required placeholder="Admin email" className={inputCls + " bg-white"} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
              <PasswordInput testid="admin-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="Password" light />
              <div className="text-right"><Link to="/admin/forgot-password" data-testid="admin-forgot-link" className="text-xs font-semibold text-[#14B8A6] hover:underline">Forgot password?</Link></div>
              <button data-testid="admin-submit" className={btnCls} disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}</button>
            </form>
          </>
        )}

        {phase === "setup" && (
          <>
            <div className="mt-3 inline-flex items-center gap-2 text-[#14B8A6]"><ShieldCheck size={18} /><h1 className="font-display text-2xl font-bold text-white">Set up 2-step verification</h1></div>
            <p className="mt-2 text-sm text-slate-300">For your security, admin sign-in requires a one-time code sent to a verified email. Confirm the email to use.</p>
            <form onSubmit={sendSetup} className="mt-5 space-y-4">
              <input data-testid="admin-2fa-setup-email" type="email" required disabled={emailLocked} placeholder="2FA email" className={inputCls + " bg-white disabled:opacity-70"} value={setupEmail} onChange={(e) => setSetupEmail(e.target.value)} />
              {emailLocked && <p className="text-xs text-slate-400">This 2FA email was assigned by a Super Admin and cannot be changed here.</p>}
              <button data-testid="admin-2fa-send" className={btnCls} disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send code"}</button>
            </form>
          </>
        )}

        {phase === "otp" && (
          <>
            <div className="mt-3 inline-flex items-center gap-2 text-[#14B8A6]"><ShieldCheck size={18} /><h1 className="font-display text-2xl font-bold text-white">Enter verification code</h1></div>
            <p className="mt-2 text-sm text-slate-300">We sent a 6-digit code to <span className="font-semibold text-white" data-testid="admin-2fa-mask">{maskEmail}</span>. It expires in 5 minutes.</p>
            <form onSubmit={verify} className="mt-5 space-y-4">
              <input data-testid="admin-2fa-otp" inputMode="numeric" autoFocus maxLength={6} required placeholder="••••••"
                className={inputCls + " bg-white text-center text-lg tracking-[0.5em]"} value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} />
              <button data-testid="admin-2fa-verify" className={btnCls} disabled={loading || otp.length !== 6}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & sign in"}</button>
            </form>
            <button type="button" data-testid="admin-2fa-resend" onClick={resend} disabled={cooldown > 0}
              className="mt-3 text-xs font-semibold text-[#14B8A6] hover:underline disabled:text-slate-500 disabled:no-underline">
              {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
            </button>
          </>
        )}

        <p className="mt-4 text-center text-xs text-slate-400">Authorised staff only. Actions are audited.</p>
      </div>
    </div>
  );
}
